import React, { useState, useEffect } from 'react';
import { MenuItem, TextField, Box } from '@mui/material';
import CustomTimePicker from '../payment/CustomTimePicker';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormInputTextArea } from '../../../form-components/FormInputTextArea';
import { useForm } from 'react-hook-form';
import { getBranchManager } from '../../../api/staff';
import { Close } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useToast } from 'use-toast-mui';
import { converterJsonToFormData } from '../../../utils/methods';
import { errorMessages } from '../../../utils/methods';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import { FormInputText } from '../../../form-components/FormInputText';
import { updateProductList, cancelProductRefund, getOrderPaymentLines, getOrderRefundLines } from '../../../api/payment';
import SplitPaymentSection, { SPLIT_METHOD_LABEL } from '../../SalesManagment/modal/enterSaleInfoModal/SplitPaymentSection';
import { useMutation, useQuery } from '@tanstack/react-query';
import CommonModalButton from '../../../form-components/CommonModalButton';
import { FormInputDate } from '../../../form-components/FormInputDate';
import useUserAuth from '../../../utils/useUserAuth';
import moment from 'moment';

import CardPaymentForm from '../payment/cardPaymentForm';
import CashPaymentForm from '../payment/cashPaymentForm';
import RegisterTypeToggle from '../../SalesManagment/modal/enterSaleInfoModal/RegisterTypeToggle';
import SellerShareSelect, { buildSellerPayload, isSellerShareValid, PENDING_SHARES } from '../../common/SellerShareSelect';
import BankTransferForm from '../payment/bankPaymentForm';
import { getPointTrainers } from '../../../api/staff';
import { FormInputMultiDropdown } from '../../form/FormInputMultiDropdown';
import { getUserPaymentDetail } from '../../../api/user';
import { getMemberPoints, getBranchPointSetting } from '../../../api/member-point';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ConfirmModal from '../../common/modal/ConfirmModal';

import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconWrapper,
  ModalContent,
  ModalFooter,
  FormRow,
  FormLabel,
  FormInputWrapper,
  CancelButton,
  RefundCancelButton,
  SaveButton,
  CurrencyUnit,
  CountUnit,
  textInputStyle,
  dropdownStyle,
  disabledInputStyle,
  dateInputStyle,
  memoTextAreaStyle,
  PointHintCaption,
  ContentSection,
  FormFieldRow,
  FormFieldGroupHalf,
  FormFieldLabel,
  dateInputStyleFlex,
  DropdownArrowIcon,
} from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModalStyled';

const payMethods = [
  { value: '카드', label: '카드' },
  { value: '계좌이체', label: '계좌이체' },
  { value: '현금', label: '현금' },
];

// 환불취소 다이얼로그 스타일
const RefundCancelLockerWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const RefundCancelWarningText = styled(DialogContentText)({
  color: '#E15241',
  fontWeight: 600,
});

const RefundCancelReasonWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// 결제 입력 영역 잠금 — 실수로 결제 정보를 건드리는 걸 막고 '수정하기'를 눌러야 열린다.
// 뒤 내용이 무엇인지는 보여야 하므로 흐림은 약하게만 준다.
const PaymentLockContent = styled(Box, { shouldForwardProp: prop => prop !== 'locked' })(({ locked }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  filter: locked ? 'blur(1.5px)' : 'none',
  opacity: locked ? 0.9 : 1,
  pointerEvents: locked ? 'none' : 'auto',
  userSelect: locked ? 'none' : 'auto',
  transition: 'filter 220ms cubic-bezier(.2,.8,.2,1), opacity 220ms cubic-bezier(.2,.8,.2,1)',
}));

const PaymentLockOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
});

const PaymentUnlockButton = styled('button')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  border: 0,
  background: 'transparent',
  padding: '16px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'transform 180ms cubic-bezier(.2, .8, .2, 1)',
  '& .unlock-icon': {
    transition: 'transform 180ms cubic-bezier(.2, .8, .2, 1), box-shadow 180ms ease-out',
  },
  '& .unlock-label': {
    transition: 'box-shadow 180ms ease-out',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  '&:hover .unlock-icon': {
    transform: 'scale(1.06)',
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.3)',
  },
  '&:hover .unlock-label': {
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:active .unlock-icon': {
    transform: 'scale(0.98)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '& .unlock-icon, & .unlock-label': { transition: 'none' },
  },
});

const PaymentUnlockIcon = styled('span')({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 10px 24px rgba(0, 0, 0, 0.22)',
});

const PaymentUnlockLabel = styled('span')({
  fontSize: '14px',
  fontWeight: 700,
  color: '#222',
  backgroundColor: '#fff',
  padding: '6px 14px',
  borderRadius: '999px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
});

// 그리드 셀 안에서 입력과 단위(원/P/회)를 한 줄로 묶는다 — 결제수정 모달과 동일한 상단 라벨 그리드용.
const UnitField = ({ children, unit }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    {unit === '원' && <CurrencyUnit>원</CurrencyUnit>}
    {unit === 'P' && <CurrencyUnit>P</CurrencyUnit>}
    {unit === '회' && <CountUnit>회</CountUnit>}
  </div>
);

const ManualDetailModalV2 = ({ ticketData, fetchData, open, onClose, readOnly = false }) => {
  const toast = useToast();
  const { user } = useSelector(state => state.user);
  const canSaleDateChange = useUserAuth('MEMBER_SALE_DATE_CHANGE');
  const [branchManager, setBranchManager] = useState(null);
  const [stockQuantity, setStockQuantity] = useState([]);
  const [usageCount, setUsageCount] = useState([]);
  const [ticketPrice, setTicketPrice] = useState([]);
  // 수정 전 판매금액. 이보다 올려 저장할 때만 확인 모달을 띄운다(금액 오타 방지).
  const [originalTicketPrice, setOriginalTicketPrice] = useState(0);
  const [overPriceConfirmOpen, setOverPriceConfirmOpen] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
  const [ticketDiscountAmount, setTicketDiscountAmount] = useState('');
  const [ticketFinalPrice, setTicketFinalPrice] = useState('');
  const [ticketOutstandingPrice, setTicketOutstandingPrice] = useState('');
  // 사용 포인트 (#49 후속) — 결제금액 = 판매가 − 할인 − 미수 − 포인트. 수정 시 서버가 잔액을 정산한다.
  const [editUsedPoint, setEditUsedPoint] = useState('0');
  // 수정 전 사용 포인트 — 최대 사용 가능치는 "보유 잔액 + 이미 이 주문에 쓴 포인트"다.
  const [originalUsedPoint, setOriginalUsedPoint] = useState(0);
  const [memberId, setMemberId] = useState(null);
  const [trainers, setTrainers] = useState(null); // 강사 목록
  const [assignedTrainerLabels, setAssignedTrainerLabels] = useState({}); // 배정 강사 id→이름 (퇴사자 옵션 병합용)
  const [selectedTicketType, setSelectedTicketType] = useState(null); // 선택된 수강권 타입
  const [orderInfo, setOrderInfo] = useState(null); // 주문 정보 상태 추가
  // 미수금 결제 건은 정가 0 이 취소 복원 경로의 판별값이라 판매금액을 고칠 수 없다(서버도 동일 규칙으로 거부).
  const isOutstandingPaymentOrder = orderInfo?.paymentStatus === 'PAID_OUTSTANDING';
  const [loading, setLoading] = useState(false); // 로딩 상태 추가
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    setError,
    reset,
    watch,
    getValues,
  } = useForm({
    defaultValues: {
      ticketPrice: '',
      paymentMethod: null,
      branchManager: null,
      sellers: [], // #122 판매정보 수정 — 판매담당자 N명(배정 순), 환불 탭은 branchManager(환불 담당자 1명) 그대로
      sellerShares: {},
      memo: '',
      courseStartDate: '',
      saleTime: '',
      refundDate: '',
      refundTime: '',
      cardCompanyCode: null,
      cardNumber: '',
      approvalNumber: '',
      receiptYn: 'Y',
      deductionType: 0,
      receiptType: 'phone',
      personalId: '',
      businessNumber: '',
      bankInfo: '',
      depositorName: '',
      trainers: [],
      courseTicketName: '',
      // 현재 화면 값들을 defaultValues에 추가
      usageCount: 0,
      stockQuantity: '0',
      ticketFinalPrice: '0',
      ticketDiscountAmount: '0',
      ticketOutstandingPrice: '0',
      installmentMonth: null,
      issuePhoneNumber: '',
      issueBusinessNumber: '',
      // 환불 관련 필드들 추가
      useAmount: '',
      penaltyAmount: '',
      refundAmount: '',
      paymentAmount: '',
      outstandingAmount: '',
    },
  });

  const updateType = ticketData && ticketData.updateType ? ticketData.updateType : '';

  const [openDialog, setOpenDialog] = useState(false);
  const [openRefundCancelDialog, setOpenRefundCancelDialog] = useState(false);
  const [refundCancelReason, setRefundCancelReason] = useState('');
  const [formValues, setFormValues] = useState(null);

  // 보유 포인트·지점 설정 (#49 후속 — 판매 정보 수정의 포인트 안내용)
  // orderdetail 응답에는 memberId가 없어 매출 행(ticketData)의 member key 로 폴백한다.
  const pointMemberId = orderInfo?.memberId ?? ticketData?.memberId;
  const pointBranchId = user?.branch?.branchId;
  const { data: memberPointData } = useQuery({
    queryKey: ['memberPointBalance', pointMemberId, pointBranchId],
    queryFn: () => getMemberPoints(pointMemberId, `branchId=${pointBranchId}&type=ALL&page=1&limit=1`, user.token),
    enabled: open && updateType === 'pay' && !!pointMemberId && !!pointBranchId && !!user?.token,
  });
  const { data: pointSetting } = useQuery({
    queryKey: ['branchPointSetting', pointBranchId],
    queryFn: () => getBranchPointSetting(pointBranchId, user.token),
    enabled: open && updateType === 'pay' && !!pointBranchId && !!user?.token,
  });
  const pointBalance = memberPointData?.balance || 0;
  const minUsePoint = pointSetting?.minUsePoint || 0;
  // 캡션의 '최대'는 지점(센터) 설정 한도만 보여준다(사용자 결정). 0 이면 최대 문구 생략.
  const toIntStr = value => parseInt(String(value ?? 0).replace(/[^\d]/g, ''), 10) || 0;
  const maxUsePoint = (() => {
    if (!pointSetting?.maxUseType || pointSetting?.maxUseValue == null) return 0;
    if (pointSetting.maxUseType === 'FIXED') return pointSetting.maxUseValue;
    const base = toIntStr(ticketPrice) - toIntStr(ticketDiscountAmount) - toIntStr(ticketOutstandingPrice);
    return Math.max(0, Math.floor((base * pointSetting.maxUseValue) / 100));
  })();

  // 라커 상품 여부는 결제 내역 응답에 추가된 subType으로 판별 (list row 우선, detail 폴백)
  const isLockerProduct = (ticketData?.subType ?? orderInfo?.subType) === 'LOCKER';

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleRefundCancelDialogClose = () => {
    setOpenRefundCancelDialog(false);
    setRefundCancelReason('');
  };

  // 주문 정보 조회 함수
  const getOrderInfo = async () => {
    if (!ticketData?.memberOrderId || !user?.token) {
      console.error('memberOrderId 또는 token이 없습니다');
      return;
    }

    try {
      setLoading(true);
      // memberOrderId를 이용해서 회원 주문 정보 조회
      const data = await getUserPaymentDetail(ticketData.memberOrderId, user.token);

      if (data && data.content && data.content.length > 0) {
        const orderData = data.content[0];
        setOrderInfo(orderData);

        // 화면에 표시할 값들을 설정
        setOrderDisplayValues(orderData);
      }
    } catch (error) {
      console.error('주문 정보 조회 실패:', error);
      toast.error('주문 정보를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 화면에 표시할 값들을 설정하는 함수
  const setOrderDisplayValues = orderData => {
    if ('pay' === updateType) {
      // #124 신규/재등록 — 서버가 고른 값(없으면 자동 판정)을 돌려준다
      setValue('registerType', orderData.registerType || null);

      // 수강권/이용권 명
      setValue('courseTicketName', orderData.courseTicketName || '');

      // 결제 방법
      setValue('paymentMethod', orderData.paymentMethod || null);

      // 판매일 + 시간
      if (orderData.paidDate) {
        setValue('courseStartDate', orderData.paidDate);
        // "2026-07-08 22:05:17" → "22:05" 추출
        const timePart = orderData.paidDate.split(' ')[1];
        if (timePart) {
          setValue('saleTime', timePart.substring(0, 5));
        }
      }

      //
      if (orderData.stockQuantity) {
        setStockQuantity(orderData.stockQuantity || '0');
      }

      // 가격 정보
      setTicketPrice(orderData.regularPrice || '0');
      setOriginalTicketPrice(toInt(orderData.regularPrice));
      setValue('ticketPrice', formatInteger(orderData.regularPrice || '0'));
      setTicketDiscountAmount(orderData.discountPrice || '0');
      setTicketFinalPrice(orderData.finalPrice || '0');
      setTicketOutstandingPrice(orderData.outstandingAmount || '0');
      setEditUsedPoint(String(orderData.usedPoint || 0));
      setOriginalUsedPoint(Number(orderData.usedPoint) || 0);
      setUsageCount(orderData.usageCount || '0');

      // 판매 담당자 — #122 배정 테이블 CSV(sellerIds/sellerRates) 우선, 없으면 REGISTERED_BY 1명. 옵션 value 가 숫자라 Number 로 맞춘다
      const sellerIds = orderData.sellerIds
        ? String(orderData.sellerIds).split(',').filter(Boolean).map(Number)
        : orderData.registeredBy
          ? [Number(orderData.registeredBy)]
          : [];
      const sellerRates = orderData.sellerRates ? String(orderData.sellerRates).split(',').map(Number) : [];
      // 비율을 먼저 넣어야 SellerShareSelect 의 선택 변경 효과가 균등 분배로 덮어쓰지 않는다.
      // N명인데 비율이 비어 있으면(SHARE_RATE NULL) "나중에 분배" 상태로 연다.
      setValue(
        'sellerShares',
        sellerIds.length < 2
          ? {}
          : sellerRates.length
            ? Object.fromEntries(sellerIds.map((id, i) => [id, sellerRates[i]]))
            : PENDING_SHARES,
      );
      setValue('sellers', sellerIds);

      // 메모
      setValue('memo', orderData.memo || '');

      // 담당 강사 (PRIVATE_LESSON인 경우) — #77 다중 배정 프리필 (trainerIds CSV 우선)
      if (orderData.classType) {
        // 주문 단위 CSV 라 다중 티켓 주문에서 같은 강사가 반복될 수 있다 — 쌍(id·이름) 기준 dedup
        const rawPrefillIds = orderData.trainerIds
          ? String(orderData.trainerIds).split(',').filter(Boolean).map(Number)
          : orderData.trainerId
            ? [Number(orderData.trainerId)]
            : [];
        const prefillTrainerIds = [...new Set(rawPrefillIds)];
        setValue('trainers', prefillTrainerIds);
        setSelectedTicketType('PRIVATE_LESSON');

        // 배정 강사 이름 맵 보관 — 재직 목록에 없는 강사(퇴사 등) 옵션 병합용.
        // trainerNames CSV 는 trainerIds 와 같은 배정 순서다.
        const rawPrefillNames = String(orderData.trainerNames || '').split(',');
        setAssignedTrainerLabels(
          Object.fromEntries(prefillTrainerIds.map(id => {
            const rawIndex = rawPrefillIds.indexOf(id); // 이름 CSV 는 raw 순서와 짝
            return [id, rawPrefillNames[rawIndex] || ''];
          })),
        );
      }

      // 라인 결제로 만든 주문은 PAYMENT_METHOD 가 CASH/TRANSFER/CARD(영문)라 한글로 맞춰서 같은 분기를 태운다
      const METHOD_KO = { CARD: '카드', CASH: '현금', TRANSFER: '계좌이체' };
      const paymentMethodKo = METHOD_KO[orderData.paymentMethod] || orderData.paymentMethod;

      // 카드 결제 정보
      if (paymentMethodKo === '카드') {
        setValue('installmentMonth', orderData.installmentMonth || null);
        setValue('cardCompanyCode', orderData.cardCompanyCode || null);
        setValue('cardNumber', orderData.cardNumber || '');
        setValue('approvalNumber', orderData.cId || '');
      }

      // 현금영수증 정보 (계좌이체, 현금) — 분할결제(MIXED)에 현금·계좌이체 라인이 섞인 주문도 주문 레벨 값을 그대로
      // 올린다. 안 올리면 다른 항목만 고쳐 저장해도 저장돼 있던 현금영수증·승인번호가 빈값으로 덮인다.
      if (paymentMethodKo === '계좌이체' || paymentMethodKo === '현금' || paymentMethodKo === 'MIXED') {
        setValue('receiptYn', orderData.receiptYn || 'Y');

        if (orderData.receiptYn === 'Y') {
          setValue('deductionType', orderData.deductionType === 'PERSONAL' ? 0 : 1);
          setValue('receiptType', orderData.receiptType || 'phone');

          if (orderData.deductionType === 'PERSONAL') {
            setValue('issuePhoneNumber', orderData.receiptValue || '');
          } else {
            setValue('issueBusinessNumber', orderData.receiptValue || '');
          }
        }
      }

      // 계좌이체 추가 정보
      if (paymentMethodKo === '계좌이체') {
        setValue('bankInfo', orderData.bankInfo || '');
        setValue('depositorName', orderData.depositorName || '');
        setValue('approvalNumber', orderData.cId || '');
      }

      // 현금 결제 추가 정보 (MIXED 는 주문 레벨 C_ID 가 비어 있고 라인 cid 에 있다 — 라인 로드 시 채운다)
      if (paymentMethodKo === '현금' || paymentMethodKo === 'MIXED') {
        setValue('approvalNumber', orderData.cId || '');
      }
    } else if (updateType === 'refund') {
      // 환불행의 paidDate 는 member_order_refund.REFUND_DATE 다.
      if (orderData.paidDate) {
        setValue('refundDate', orderData.paidDate);
        const refundTimePart = orderData.paidDate.split(' ')[1];
        if (refundTimePart) {
          setValue('refundTime', refundTimePart.substring(0, 5));
        }
      }
      setValue('useAmount', formatInteger(orderData.refundUseAmount || '0'));
      setValue('penaltyAmount', formatInteger(orderData.refundPenaltyAmount || '0'));
      setValue('paymentAmount', formatInteger(orderData.finalPrice || '0'));
      setValue('outstandingAmount', formatInteger(orderData.outstandingAmount || '0'));

      setValue('paymentMethod', orderData.refundMethod || null);
      const METHOD_KO_REFUND = { CARD: '카드', CASH: '현금', TRANSFER: '계좌이체' };
      const refundMethodKo = METHOD_KO_REFUND[orderData.refundMethod] || orderData.refundMethod;

      // 환불 담당자
      setValue('branchManager', orderData.refundManagerId || null);

      // 메모
      setValue('memo', orderData.refundMemo || '');

      // 카드 결제 정보
      if (refundMethodKo === '카드') {
        setValue('installmentMonth', orderData.refundInstallmentMonth || null);
        setValue('cardCompanyCode', orderData.refundCardCompanyCode || null);
        setValue('cardNumber', orderData.refundCardNumber || '');
        setValue('approvalNumber', orderData.refundCId || '');
      }

      // 현금영수증 정보 (계좌이체, 현금)
      if (refundMethodKo === '계좌이체' || refundMethodKo === '현금' || refundMethodKo === 'MIXED') {
        setValue('receiptYn', orderData.refundReceiptYn || 'Y');

        if (orderData.refundReceiptYn === 'Y') {
          setValue('deductionType', orderData.refundDeductionType === 'PERSONAL' ? 0 : 1);
          setValue('receiptType', orderData.refundReceiptType || 'phone');

          if (orderData.refundDeductionType === 'PERSONAL') {
            setValue('issuePhoneNumber', orderData.refundReceiptValue || '');
          } else {
            setValue('issueBusinessNumber', orderData.refundReceiptValue || '');
          }
        }
      }

      // 계좌이체 추가 정보
      if (refundMethodKo === '계좌이체') {
        setValue('bankInfo', orderData.refundBankInfo || '');
        setValue('depositorName', orderData.refundDepositorName || '');
        setValue('approvalNumber', orderData.refundCId || '');
      }

      // 현금 결제 추가 정보
      if (refundMethodKo === '현금' || refundMethodKo === 'MIXED') {
        setValue('approvalNumber', orderData.refundCId || '');
      }
    }
  };

  // 이용금액이나 위약금이 변경될 때마다 환불금액 자동 계산
  const watchUseAmount = watch('useAmount');
  const watchPenaltyAmount = watch('penaltyAmount');
  const watchPaymentAmount = watch('paymentAmount');
  const watchOutstandingAmount = watch('outstandingAmount');

  useEffect(() => {
    if (ticketData?.updateType === 'refund') {
      const useAmount = Number(watchUseAmount?.toString().replace(/,/g, '') || 0);
      const penaltyAmount = Number(watchPenaltyAmount?.toString().replace(/,/g, '') || 0);
      const paymentAmount = Number(watchPaymentAmount?.toString().replace(/,/g, '') || 0);
      const outstandingAmount = Number(watchOutstandingAmount?.toString().replace(/,/g, '') || 0);

      // const refundAmount = paymentAmount - useAmount - penaltyAmount - outstandingAmount;
      const refundAmount = paymentAmount - useAmount - penaltyAmount;
      setValue('refundAmount', formatInteger(Math.max(0, refundAmount)));
    }
  }, [
    watchUseAmount,
    watchPenaltyAmount,
    watchPaymentAmount,
    watchOutstandingAmount,
    setValue,
    ticketData?.updateType,
  ]);

  const handleClose = (e, r) => {
    if (r === 'backdropClick') {
      return; // 백드롭 클릭으로 닫히는 것을 방지
    }

    setUsageCount(0);
    setStockQuantity('0');
    setTicketPrice([]);
    setTicketDiscountAmount('');
    setTicketFinalPrice('');
    setTicketOutstandingPrice('');
    setSelectedTicketType(null); // 수강권 타입 초기화
    setOrderInfo(null); // 주문 정보 초기화
    reset({
      ticketPrice: '',
      paymentMethod: null,
      branchManager: null,
      sellers: [],
      sellerShares: {},
      memo: '',
      courseStartDate: '',
      saleTime: '',
      cardCompanyCode: null,
      cardNumber: '',
      approvalNumber: '',
      receiptYn: 'Y',
      deductionType: 0,
      receiptType: 'phone',
      personalId: '',
      businessNumber: '',
      bankInfo: '',
      depositorName: '',
      trainers: [],
      courseTicketName: '',
      usageCount: 0,
      stockQuantity: '0',
      ticketFinalPrice: '0',
      ticketDiscountAmount: '0',
      ticketOutstandingPrice: '0',
      installmentMonth: null,
      issuePhoneNumber: '',
      issueBusinessNumber: '',
      // 환불 관련 필드들 초기화
      useAmount: '',
      penaltyAmount: '',
      refundAmount: '',
      paymentAmount: '',
      outstandingAmount: '',
    });

    setFormValues(null);
    onClose();
  };

  // 저장 클릭 진입점. 판매금액을 수정 전보다 올렸으면 확인부터 받는다(금액 오타 방지).
  // toInt 는 [^\d] 를 지워 부호까지 날린다 — 음수 판정에는 부호를 살리는 파서를 쓴다.
  const toSignedInt = value => Number(String(value ?? 0).replace(/,/g, '')) || 0;

  const handleSaveClick = values => {
    // 저장 버튼은 항상 누를 수 있게 두고(잠금 상태와 무관), 합계가 안 맞는 저장만 여기서 막는다.
    if (splitSaveBlocked) {
      toast.error('결제수단 합계가 결제금액과 다릅니다. 결제수단 추가를 눌러 반영해주세요');
      return;
    }
    // 할인+미수금+포인트가 판매금액을 넘으면 결제금액이 음수가 된다. 백엔드에 금액 검증이 없어
    // 그대로 저장되므로 여기서 막는다.
    if (ticketData?.updateType === 'pay' && toSignedInt(ticketFinalPrice) < 0) {
      toast.error('할인금액과 미수금의 합이 판매금액보다 큽니다. 금액을 확인해주세요');
      return;
    }
    // #122 판매담당자 — 판매등록과 같은 규칙(1명 이상, N명이면 비율 합 100)
    if (ticketData?.updateType === 'pay' && !values.sellers?.length) {
      toast.error('판매 담당자를 선택해주세요');
      return;
    }
    if (ticketData?.updateType === 'pay' && !isSellerShareValid(values.sellers, values.sellerShares)) {
      toast.error('판매 담당자 귀속 비율의 합은 100% 여야 합니다');
      return;
    }
    if (ticketData?.updateType === 'pay' && toInt(ticketPrice) > originalTicketPrice) {
      setPendingSubmitValues(values);
      setOverPriceConfirmOpen(true);
      return;
    }
    submit(values);
  };

  const submit = async values => {
    if (!ticketData.memberOrderId) {
      return setError('courseTicket', {
        type: 'custom',
        message: 'Please select a course ticket or pass',
      });
    }

    if (ticketData?.updateType === 'refund' && values.refundDate) {
      const refundDatePart = moment(values.refundDate).format('YYYY-MM-DD');
      const refundTimePart = values.refundTime || '00:00';
      values.refundDateTime = `${refundDatePart} ${refundTimePart}:00`;
    }

    if (ticketData?.updateType === 'pay' && values.courseStartDate) {
      const datePart = moment(values.courseStartDate).format('YYYY-MM-DD');
      const timePart = values.saleTime || '00:00';
      values.courseStartDate = `${datePart} ${timePart}:00`;
    } else if (ticketData?.updateType === 'pay' && !values.courseStartDate) {
      toast.error('판매일을 입력해주세요');
      return setError('courseStartDate', {
        type: 'custom',
        message: '판매일을 입력해주세요',
      });
    }

    setFormValues(values);
    setOpenDialog(true);
  };

  const handleRefundCancel = () => {
    setOpenRefundCancelDialog(true);
  };

  // 환불취소 ErrorCode별 사용자 메시지
  const refundCancelErrorMessages = {
    STOCK_INSUFFICIENT: '재고가 부족합니다. 재고를 채운 후 다시 시도하세요.',
    TRIAL_LIMIT_EXCEEDED: '체험권 판매 한도를 초과했습니다.',
    TICKET_ALREADY_EXPIRED: '이미 만료된 이용권은 환불취소할 수 없습니다.',
    INVALID_PAYMENT_PROVIDER: '앱 결제 건은 환불취소할 수 없습니다.',
  };

  // ── 분할결제(#49) 라인 편집 ──
  // 주문/환불이 복합(라인 2개 이상)이면 단일 수단 폼 대신 분할 라인 편집기를 보여준다.
  const [splitEditLines, setSplitEditLines] = useState([]);
  const [splitEditMode, setSplitEditMode] = useState(false);
  // 결제 입력 영역은 열 때마다 잠근 상태로 시작한다.
  const [paymentLocked, setPaymentLocked] = useState(true);
  useEffect(() => {
    if (open) setPaymentLocked(true);
  }, [open]);
  useEffect(() => {
    if (!open || !ticketData?.memberOrderId || !user?.token) {
      setSplitEditMode(false);
      setSplitEditLines([]);
      return;
    }
    const fetcher = ticketData?.updateType === 'refund' ? getOrderRefundLines : getOrderPaymentLines;
    fetcher(ticketData.memberOrderId, user.token)
      .then(lines => {
        const list = Array.isArray(lines) ? lines : [];
        // 단일 수단 주문도 같은 편집기 UI 로 통일한다(사용자 결정) — 라인 유무와 무관하게 항상 편집기.
        setSplitEditMode(true);
        // 현금·계좌이체 승인번호는 라인 cid 에도 실려 있다(분할결제면 주문 레벨 C_ID 가 비어 라인이 유일한 원천).
        // 폼이 비어 있을 때만 채워 저장 시 빈값으로 덮이지 않게 한다.
        const cashLike = list.find(
          line => ['CASH', 'TRANSFER'].includes(line.paymentMethod || line.refundMethod) && (line.cid || line.cId),
        );
        if (cashLike && !getValues('approvalNumber')) setValue('approvalNumber', cashLike.cid || cashLike.cId);
        setSplitEditLines(
          list.map((line, i) => ({
            key: `loaded-${i}`,
            method: line.paymentMethod || line.refundMethod,
            amount: Number(line.amount) || 0,
            cardCompanyCode: line.cardCompanyCode || null,
            cardNumber: line.cardNumber || '',
            cid: line.cid || line.cId || '',
            installmentMonth: line.installmentMonth || null,
            bankInfo: line.bankInfo || '',
            depositorName: line.depositorName || '',
            // 환불 라인 재기록 시 수납 라인 참조 보존
            orderPaymentId: line.orderPaymentId || null,
          })),
        );
      })
      .catch(() => {
        setSplitEditMode(true);
        setSplitEditLines([]);
      });
  }, [open, ticketData?.memberOrderId, ticketData?.updateType, user?.token]);

  // 라인 없는 구주문 — 주문 구컬럼(수단·카드정보)으로 1라인을 합성해 편집기에 보여준다.
  const METHOD_TO_ENUM = { 카드: 'CARD', 현금: 'CASH', 계좌이체: 'TRANSFER', CARD: 'CARD', CASH: 'CASH', TRANSFER: 'TRANSFER' };
  useEffect(() => {
    if (!splitEditMode || splitEditLines.length > 0 || !orderInfo) return;
    const isRefund = ticketData?.updateType === 'refund';
    const method = METHOD_TO_ENUM[isRefund ? orderInfo.refundMethod : orderInfo.paymentMethod];
    const amount = Number(String((isRefund ? orderInfo.refundAmount : orderInfo.ticketFinalPrice ?? orderInfo.finalPrice) || 0).replace(/,/g, ''));
    if (!method || !(amount > 0)) return;
    setSplitEditLines([
      {
        key: 'synth-0',
        method,
        amount,
        cardCompanyCode: (isRefund ? orderInfo.refundCardCompanyCode : orderInfo.cardCompanyCode) || null,
        cardNumber: (isRefund ? orderInfo.refundCardNumber : orderInfo.cardNumber) || '',
        cid: (isRefund ? orderInfo.refundCId : orderInfo.cId) || '',
        installmentMonth: (isRefund ? orderInfo.refundInstallmentMonth : orderInfo.installmentMonth) || null,
        bankInfo: (isRefund ? orderInfo.refundBankInfo : orderInfo.bankInfo) || '',
        depositorName: (isRefund ? orderInfo.refundDepositorName : orderInfo.depositorName) || '',
        orderPaymentId: null,
      },
    ]);
    // splitEditMode 는 라인 fetch 완료 후에만 true 가 되므로 fetch 가 합성 라인을 덮어쓸 일은 없다.
    // length 를 의존성에 넣으면 사용자가 라인을 전부 지웠을 때 합성이 라인을 되살리므로 넣지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitEditMode, orderInfo]);

  // 편집본 → 서버 payments JSON (판매등록 buildPayLines 와 동일 계약)
  const buildEditedLines = () =>
    splitEditLines.map(line => ({
      method: line.method,
      amount: line.amount,
      ...(line.orderPaymentId && { orderPaymentId: line.orderPaymentId }),
      ...(line.method === 'CARD' && {
        installmentMonth: line.installmentMonth || null,
        cardCompanyCode: line.cardCompanyCode || null,
        cardNumber: line.cardNumber || null,
        cid: line.cid || null,
      }),
      ...(line.method === 'TRANSFER' && {
        bankInfo: line.bankInfo || null,
        depositorName: line.depositorName || null,
      }),
      // 현금·계좌이체 승인번호는 주문 레벨 입력(현금영수증 폼). 서버가 단일 라인이면 라인 cid 로
      // member_order.C_ID 를 덮어쓰므로 라인에도 실어야 남는다.
      ...((line.method === 'CASH' || line.method === 'TRANSFER') && {
        cid: watch('approvalNumber') || null,
      }),
    }));
  const splitHasCashLike = splitEditLines.some(line => line.method === 'CASH' || line.method === 'TRANSFER');

  const editedRepresentative = () => {
    if (splitEditLines.length === 0) return null; // 라인 없음 — 기존 수단 유지
    return splitEditLines.length === 1
      ? SPLIT_METHOD_LABEL[splitEditLines[0].method] || splitEditLines[0].method
      : 'MIXED';
  };

  // 라인 합계가 금액(pay=결제금액, refund=환불금액)과 일치해야만 저장 가능(사용자 결정).
  const splitEditTotal = splitEditLines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const splitExpectedTotal =
    Number(
      String(
        (ticketData?.updateType === 'refund' ? watch('refundAmount') : watch('ticketFinalPrice')) || 0,
      ).replace(/,/g, ''),
    ) || 0;
  const splitSaveBlocked = splitEditMode && splitEditTotal !== splitExpectedTotal;

  const cancelRefundMutation = useMutation({
    mutationFn: payload => cancelProductRefund(payload, user.token),
    onSuccess: () => {
      toast.success('환불 취소가 완료되었습니다');
      handleRefundCancelDialogClose();
      handleClose();
      fetchData();
    },
    onError: error => {
      const code = error?.response?.data?.code;
      if (code && refundCancelErrorMessages[code]) {
        toast.error(refundCancelErrorMessages[code]);
      } else {
        toast.error(errorMessages(error));
      }
    },
  });

  const handleConfirmRefundCancel = () => {
    const registeredBy = watch('branchManager');
    if (!registeredBy) {
      toast.error('판매 담당자를 확인해주세요');
      return;
    }

    cancelRefundMutation.mutate({
      memberOrderId: ticketData?.memberOrderId,
      registeredBy,
      ...(refundCancelReason && { reason: refundCancelReason }),
    });
  };

  const handleConfirmSubmit = async () => {
    try {
      const request = {
        memberOrderId: ticketData?.memberOrderId,
        updateType: ticketData?.updateType,
        paymentMethod: (splitEditMode && editedRepresentative()) || formValues.paymentMethod,
        // 분할 편집본 — 서버가 라인 전체를 이 값으로 교체한다(합계=금액 검증). 라인이 없으면 기존 방식 유지.
        ...(splitEditMode && splitEditLines.length > 0 &&
          ticketData?.updateType === 'pay' && { payments: JSON.stringify(buildEditedLines()) }),
        ...(splitEditMode && splitEditLines.length > 0 &&
          ticketData?.updateType === 'refund' && { refundPayments: JSON.stringify(buildEditedLines()) }),
        registeredBy: formValues.branchManager,
        ...(ticketData?.updateType === 'pay' && {
          // #122 판매담당자 N명+비율 — 멀티파트라 CSV. registeredBy 는 첫 담당자(구 서버 호환)
          ...(() => {
            const seller = buildSellerPayload(formValues.sellers, formValues.sellerShares);
            return seller.registeredBy
              ? {
                  registeredBy: seller.registeredBy,
                  registeredByIds: seller.registeredByIds.join(','),
                  shareRates: seller.shareRates.join(','),
                }
              : {};
          })(),
          saleStartDate: formValues.courseStartDate,
          registerType: formValues.registerType || null, // #124
        }),
        // PRIVATE_LESSON일 때 담당 강사 추가 (#77 다중 — trainerId 는 구서버 호환용 첫 강사)
        ...(selectedTicketType === 'PRIVATE_LESSON' && {
          trainerId:
            Array.isArray(formValues.trainers) && formValues.trainers.length
              ? formValues.trainers[0]
              : null,
          // multipart 폼 전송이라 배열이 서버 HashMap 에 List 로 도착하지 않는다 — CSV 로 보낸다
          trainerIds:
            Array.isArray(formValues.trainers) && formValues.trainers.length
              ? formValues.trainers.join(',')
              : null,
        }),
        // pay 타입일 때의 가격 정보
        ...(ticketData?.updateType === 'pay' && {
          ticketPrice: Number(formValues.ticketPrice.toString().replace(/,/g, '')),
          ticketDiscountAmount: Number(formValues.ticketDiscountAmount.toString().replace(/,/g, '')),
          ticketFinalPrice: Number(formValues.ticketFinalPrice.toString().replace(/,/g, '')),
          ticketOutstandingPrice: Number(formValues.ticketOutstandingPrice.toString().replace(/,/g, '')),
          // 사용 포인트 (#49 후속) — 서버가 기존값과의 차액만큼 잔액을 정산한다
          usedPoint: Number(String(formValues.usedPoint || 0).replace(/,/g, '')),
          usageCount: formValues.usageCount,
        }),
        // refund 타입일 때의 환불 정보
        ...(ticketData?.updateType === 'refund' && {
          // 환불일시를 바꾸지 않았으면 서버가 기존 환불일을 그대로 둔다.
          ...(formValues.refundDateTime && { refundDateTime: formValues.refundDateTime }),
          useAmount: Number(formValues.useAmount?.toString().replace(/,/g, '') || 0),
          penaltyAmount: Number(formValues.penaltyAmount?.toString().replace(/,/g, '') || 0),
          refundAmount: Number(formValues.refundAmount?.toString().replace(/,/g, '') || 0),
          paymentAmount: Number(formValues.paymentAmount?.toString().replace(/,/g, '') || 0),
          outstandingAmount: Number(formValues.outstandingAmount?.toString().replace(/,/g, '') || 0),
        }),
        memo: formValues.memo,
        // 라인 편집 모드 — 현금·계좌이체 라인이 있으면 현금영수증 정보를 주문 레벨로 보낸다(판매등록과 동일 계약)
        ...(splitEditMode && splitHasCashLike && {
          ...(formValues.receiptYn === 'Y' && {
            deductionType: formValues.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
            receiptValue:
              formValues.deductionType === 0 ? formValues.issuePhoneNumber : formValues.issueBusinessNumber || null,
            receiptType: formValues.deductionType === 0 ? formValues.receiptType : 'BUSINESS',
          }),
          receiptYn: formValues.receiptYn || 'N',
          cId: formValues.approvalNumber || null,
        }),
        // 카드 결제 정보 추가
        ...(!splitEditMode && formValues.paymentMethod === '카드' && {
          installmentMonth: formValues.installmentMonth || null,
          cardCompanyCode: formValues.cardCompanyCode || null,
          cardNumber: formValues.cardNumber || null,
          cId: formValues.approvalNumber || null,
        }),

        // 계좌이체 결제 시 추가되는 정보
        ...(!splitEditMode && formValues.paymentMethod === '계좌이체' && {
          // 현금영수증 발행 여부에 따른 처리
          ...(formValues.receiptYn === 'Y' && {
            // 개인/사업자 구분에 따른 처리
            deductionType: formValues.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            // 식별번호 (개인: 휴대폰/주민번호, 사업자: 사업자등록번호)
            receiptValue:
              formValues.deductionType === 0 ? formValues.issuePhoneNumber : formValues.issueBusinessNumber || null,
            // 식별 타입 (개인: phone/resident, 사업자: BUSINESS)
            receiptType: formValues.deductionType === 0 ? formValues.receiptType : 'BUSINESS' || null,
          }),
          // 입금 정보
          receiptYn: formValues.receiptYn || null,
          bankInfo: formValues.bankInfo || null,
          depositorName: formValues.depositorName || null,
          cId: formValues.approvalNumber || null,
        }),

        // 현금 결제 시 추가되는 정보
        ...(!splitEditMode && formValues.paymentMethod === '현금' && {
          // 현금영수증 발행 여부에 따른 처리
          ...(formValues.receiptYn === 'Y' && {
            // 개인/사업자 구분에 따른 처리
            deductionType: formValues.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            // 식별번호 (개인: 휴대폰/주민번호, 사업자: 사업자등록번호)
            receiptValue:
              formValues.deductionType === 0 ? formValues.issuePhoneNumber : formValues.issueBusinessNumber || null,
            // 식별 타입 (개인: phone/resident, 사업자: BUSINESS)
            receiptType: formValues.deductionType === 0 ? formValues.receiptType : 'BUSINESS' || null,
          }),
          // 입금 정보
          receiptYn: formValues.receiptYn || null,
          cId: formValues.approvalNumber || null,
        }),
      };

      const formData = converterJsonToFormData(request);

      await updateProductList(formData, user.token);

      toast.success('등록되었습니다');
      handleDialogClose();
      handleClose();
      fetchData();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  //판매 담당자 조회
  const getManager = async () => {
    try {
      const data = await getBranchManager(user.branch?.branchId, user.token);
      const branchManagerArray = data.map(item => ({
        value: item.managerId,
        label: item.managerName,
      }));
      setBranchManager(branchManagerArray);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  //담당 강사 조회
  const getTrainers = async () => {
    try {
      let param = user.branch?.branchId + '&courseClassType=PRIVATE_LESSON';
      const data = await getPointTrainers(param, user.token);
      const trainerArray = [
        { value: '', label: '담당 강사를 선택해주세요' }, // 기본 옵션 추가
        ...data.map(item => ({
          value: item.trainerId,
          label: item.trainerName,
        })),
      ];

      setTrainers(trainerArray);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 결제금액 = 판매가 − 할인 − 미수 − 사용 포인트 (생성 경로와 동일 계약 — 포인트 주문에서 결제금액이 어긋나던 것 수정)
  const toInt = value => parseInt(String(value ?? 0).replace(/[^\d]/g, ''), 10) || 0;
  const recalcFinalPrice = ({ discount, outstanding, point }) => {
    const dis = discount !== undefined ? discount : toInt(ticketDiscountAmount);
    const out = outstanding !== undefined ? outstanding : toInt(ticketOutstandingPrice);
    const pt = point !== undefined ? point : toInt(editUsedPoint);
    setTicketFinalPrice(toInt(ticketPrice) - (dis + out + pt));
  };

  // 판매금액 직접 편집. 현장에서 정가와 다른 금액으로 판 건을 사후 정정한다.
  // 할인·미수금·포인트는 그대로 두고 결제금액만 다시 계산한다.
  // 할인·미수금·포인트는 그대로 두고 결제금액만 다시 계산한다.
  // 여기서 할인을 자동으로 깎으면 안 된다. 한 글자씩 입력되는 동안 중간값으로 할인이 뭉개져
  // 복구되지 않는다. 결제금액이 음수가 되는 조합은 저장 단계(handleSaveClick)에서 막는다.
  function changeTicketPrice(value) {
    const price = toInt(value);
    setTicketPrice(price);
    setTicketFinalPrice(price - (toInt(ticketDiscountAmount) + toInt(ticketOutstandingPrice) + toInt(editUsedPoint)));
  }

  function changeTicketFinalPrice(value) {
    const dis = toInt(value);
    setTicketDiscountAmount(dis);
    recalcFinalPrice({ discount: dis });
  }

  // ticketOutstandingPrice 변경 함수 추가
  function changeTicketOutstandingPrice(value) {
    const out = toInt(value);
    setTicketOutstandingPrice(out);
    recalcFinalPrice({ outstanding: out });
  }

  function changeEditUsedPoint(value) {
    const pt = toInt(value);
    setEditUsedPoint(String(pt));
    recalcFinalPrice({ point: pt });
  }

  function formatInteger(value) {
    if (!value && value !== 0) {
      return '0';
    }

    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 모달이 열릴 때 주문 정보를 조회
  useEffect(() => {
    if (open && ticketData?.memberOrderId) {
      getOrderInfo();
    }
  }, [open, ticketData?.memberOrderId]);

  useEffect(() => {
    if (user.branch?.branchId) {
      getManager();
      getTrainers(); // 강사 목록 조회 추가
    }
  }, [user.headBranch, user.branch]);

  useEffect(() => {
    setValue('usageCount', usageCount);
    setValue('stockQuantity', stockQuantity);
    setValue('ticketPrice', formatInteger(ticketPrice));
    setValue('ticketFinalPrice', formatInteger(ticketFinalPrice));
    setValue('ticketDiscountAmount', formatInteger(ticketDiscountAmount));
    setValue('ticketOutstandingPrice', formatInteger(ticketOutstandingPrice));
    setValue('usedPoint', formatInteger(editUsedPoint));
  }, [
    ticketPrice,
    ticketFinalPrice,
    ticketDiscountAmount,
    usageCount,
    ticketOutstandingPrice,
    stockQuantity,
    editUsedPoint,
  ]);

  return (
    <div style={{ display: 'inline-block' }}>
      <StyledModal
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <ModalContainer>
          {/* 헤더 영역 */}
          <ModalHeader>
            <ModalHeaderTitle>{updateType === 'refund' ? '환불 정보 수정' : '판매 정보 수정'}</ModalHeaderTitle>
            <CloseIconWrapper onClick={handleClose}>
              <Close sx={{ width: '20px', height: '20px' }} />
            </CloseIconWrapper>
          </ModalHeader>

          {/* 컨텐츠 영역 */}
          <ModalContent>
            {loading ? (
              <FormRow style={{ justifyContent: 'center', height: '200px', alignItems: 'center' }}>
                정보를 불러오는 중...
              </FormRow>
            ) : (
              <>
                {/* pay — 판매등록 모달과 같은 카드+2컬럼 그리드 배치 (사용자 결정) */}
                {ticketData?.updateType === 'pay' && (
                  <>
                    {/* 카드 1 — 상품 정보 */}
                    <ContentSection sx={{ marginBottom: '12px' }}>
                      {/* #124 신규등록/재등록 — 판매등록 모달과 같은 라디오 카드, 거래일시 위 */}
                      <RegisterTypeToggle control={control} setValue={setValue} readOnly={readOnly} />
                      {/* 거래일시 + 판매 담당자 — 판매등록의 판매일시 옆 담당자 배치와 통일 */}
                      <div style={{ marginBottom: '16px', display: 'flex', gap: '16px' }}>
                        <div style={{ flex: 1.8, minWidth: 0 }}>
                          <FormFieldLabel>거래일시</FormFieldLabel>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minWidth: 0 }}>
                            <FormInputDate
                              name="courseStartDate"
                              control={control}
                              rules={{ required: true }}
                              disabled={readOnly || !canSaleDateChange}
                              placeholder={t('DD/MM/YYYY')}
                              sx={{ flex: 1, minWidth: 0 }}
                              renderInput={params => (
                                <TextField
                                  size="small"
                                  error={!!errors.courseStartDate}
                                  variant="outlined"
                                  {...params}
                                  sx={{ ...dateInputStyleFlex, width: '100%' }}
                                />
                              )}
                            />
                            <CustomTimePicker
                              value={watch('saleTime')}
                              onChange={val => setValue('saleTime', val)}
                              disabled={readOnly || !canSaleDateChange}
                              sx={{ flexShrink: 0 }}
                            />
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
                          {/* #122 판매담당자 N명 + 비율 — 판매등록과 같은 부품 */}
                          <SellerShareSelect
                            control={control}
                            setValue={setValue}
                            name="sellers"
                            sharesName="sellerShares"
                            options={branchManager || []}
                            placeholder={t('담당자 선택')}
                            disabled={readOnly}
                          />
                        </div>
                      </div>

                      <div style={{ marginBottom: selectedTicketType === 'PRIVATE_LESSON' ? '16px' : 0 }}>
                        <FormFieldLabel>{ticketData.payType === 'TICKET' ? '수강권/이용권 명' : '판매상품'}</FormFieldLabel>
                        <FormInputText
                          control={control}
                          name="courseTicketName"
                          disabled={true}
                          sx={{ ...disabledInputStyle, width: '100%' }}
                        />
                      </div>

                      {selectedTicketType === 'PRIVATE_LESSON' && (
                        <div>
                          <FormFieldLabel>{t('담당 강사')}</FormFieldLabel>
                          {/* dropdownStyle 은 flex:1 이라 flex 컨테이너 밖에선 내용폭으로 쪼그라든다 — 전폭 지정 */}
                          <FormInputMultiDropdown
                            control={control}
                            disabled={readOnly}
                            sx={{ ...dropdownStyle, width: '100%' }}
                            name="trainers"
                            options={(() => {
                              // 배정돼 있지만 재직 목록에 없는 강사(퇴사 등) — 옵션에 없으면 표시·해제 불가
                              const merged = [...(trainers || [])];
                              Object.entries(assignedTrainerLabels).forEach(([id, name]) => {
                                const numericId = Number(id);
                                if (!merged.some(option => option.value === numericId)) {
                                  merged.push({ value: numericId, label: `${name || '알 수 없음'} (퇴사)` });
                                }
                              });
                              return merged;
                            })()}
                            placeholder="담당 강사를 선택해주세요"
                          />
                        </div>
                      )}
                    </ContentSection>

                    {/* 카드 2 — 금액·포인트 */}
                    <ContentSection sx={{ marginBottom: '12px' }}>
                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>{ticketData.payType === 'TICKET' ? t('Course Ticket Amount') : '상품금액'}</FormFieldLabel>
                          <UnitField unit="원">
                            <FormInputText
                              control={control}
                              onChange={e => changeTicketPrice(e.target.value)}
                              disabled={readOnly || isOutstandingPaymentOrder}
                              name="ticketPrice"
                              value={formatInteger(ticketPrice)}
                              placeholder={t('000원')}
                              sx={readOnly ? disabledInputStyle : textInputStyle}
                            />
                          </UnitField>
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>{t('Discounted Price')}</FormFieldLabel>
                          <UnitField unit="원">
                            <FormInputText
                              control={control}
                              onChange={e => changeTicketFinalPrice(e.target.value)}
                              disabled={readOnly}
                              name="ticketDiscountAmount"
                              value={ticketDiscountAmount}
                              placeholder="0"
                              sx={readOnly ? disabledInputStyle : textInputStyle}
                            />
                          </UnitField>
                        </FormFieldGroupHalf>
                      </FormFieldRow>

                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>{t('Outstanding Amount')}</FormFieldLabel>
                          <UnitField unit="원">
                            <FormInputText
                              control={control}
                              onChange={e => changeTicketOutstandingPrice(e.target.value)}
                              disabled={readOnly}
                              name="ticketOutstandingPrice"
                              value={formatInteger(ticketOutstandingPrice)}
                              placeholder="0"
                              sx={readOnly ? disabledInputStyle : textInputStyle}
                            />
                          </UnitField>
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf>
                          {ticketData?.payType === 'TICKET' ? (
                            <>
                              <FormFieldLabel>{t('Number of Lessons')}</FormFieldLabel>
                              <UnitField unit="회">
                                <FormInputText
                                  control={control}
                                  disabled={true}
                                  name="usageCount"
                                  value={usageCount}
                                  sx={readOnly ? disabledInputStyle : textInputStyle}
                                />
                              </UnitField>
                            </>
                          ) : ticketData?.payType === 'PRODUCT' ? (
                            <>
                              <FormFieldLabel>{t('Inventory')}</FormFieldLabel>
                              <FormInputText
                                control={control}
                                name="stockQuantity"
                                disabled={readOnly}
                                value={stockQuantity}
                                sx={readOnly ? disabledInputStyle : textInputStyle}
                              />
                            </>
                          ) : null}
                        </FormFieldGroupHalf>
                      </FormFieldRow>

                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>보유 포인트</FormFieldLabel>
                          <UnitField unit="P">
                            <FormInputText
                              control={control}
                              disabled={true}
                              name="pointBalanceDisplay"
                              staticValue={formatInteger(pointBalance)}
                              sx={disabledInputStyle}
                            />
                          </UnitField>
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf>
                          <FormFieldLabel
                            title={[
                              minUsePoint > 0 && `최소 ${formatInteger(minUsePoint)}P`,
                              maxUsePoint > 0 && `최대 ${formatInteger(maxUsePoint)}P`,
                            ]
                              .filter(Boolean)
                              .join(' · ') || undefined}
                            sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                            사용 포인트
                            {(minUsePoint > 0 || maxUsePoint > 0) && (
                              <PointHintCaption sx={{ ml: '4px', mt: 0 }}>
                                (
                                {[
                                  minUsePoint > 0 && `최소 ${formatInteger(minUsePoint)}P`,
                                  maxUsePoint > 0 && `최대 ${formatInteger(maxUsePoint)}P`,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                                )
                              </PointHintCaption>
                            )}
                          </FormFieldLabel>
                          <UnitField unit="P">
                            <FormInputText
                              control={control}
                              onChange={e => changeEditUsedPoint(e.target.value)}
                              disabled={readOnly}
                              name="usedPoint"
                              value={formatInteger(editUsedPoint)}
                              placeholder="0"
                              sx={readOnly ? disabledInputStyle : textInputStyle}
                            />
                          </UnitField>
                        </FormFieldGroupHalf>
                      </FormFieldRow>

                      <FormFieldRow>
                        <FormFieldGroupHalf>
                          <FormFieldLabel>{t('Amount of payment')}</FormFieldLabel>
                          <UnitField unit="원">
                            {/* 수기 수정 가능(사용자 결정) — 할인/미수/포인트를 바꾸면 자동계산이 다시 덮어쓴다 */}
                            <FormInputText
                              control={control}
                              onChange={e => setTicketFinalPrice(toInt(e.target.value))}
                              disabled={readOnly}
                              name="ticketFinalPrice"
                              value={formatInteger(ticketFinalPrice)}
                              sx={readOnly ? disabledInputStyle : textInputStyle}
                            />
                          </UnitField>
                        </FormFieldGroupHalf>
                        <FormFieldGroupHalf />
                      </FormFieldRow>
                    </ContentSection>
                  </>
                )}

                {/* refund — 결제수정 환불 탭과 같은 카드+2컬럼 그리드 배치 (사용자 결정) */}
                {ticketData?.updateType === 'refund' && (
                  <ContentSection sx={{ marginBottom: '12px' }}>
                    {/* 환불일시 + 판매 담당자 — 판매등록의 판매일시 옆 담당자 배치와 통일 */}
                    <div style={{ marginBottom: '16px', display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1.8, minWidth: 0 }}>
                        <FormFieldLabel>환불일시</FormFieldLabel>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minWidth: 0 }}>
                          <FormInputDate
                            name="refundDate"
                            control={control}
                            disabled={readOnly}
                            placeholder={t('DD/MM/YYYY')}
                            sx={{ flex: 1, minWidth: 0 }}
                            renderInput={params => (
                              <TextField size="small" variant="outlined" {...params} sx={{ ...dateInputStyleFlex, width: '100%' }} />
                            )}
                          />
                          <CustomTimePicker
                            value={watch('refundTime')}
                            onChange={val => setValue('refundTime', val)}
                            disabled={readOnly}
                            sx={{ flexShrink: 0 }}
                          />
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
                        <FormInputDropdown
                          control={control}
                          disabled={readOnly}
                          sx={{ ...dropdownStyle, width: '100%' }}
                          rules={{ required: true }}
                          name="branchManager"
                          options={branchManager}
                          disabledItem={<MenuItem disabled>{t('담당자 선택')}</MenuItem>}
                          IconComponent={DropdownArrowIcon}
                        />
                      </div>
                    </div>

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>이용금액</FormFieldLabel>
                        <UnitField unit="원">
                          <FormInputText
                            control={control}
                            disabled={readOnly}
                            name="useAmount"
                            placeholder="이용금액"
                            sx={readOnly ? disabledInputStyle : textInputStyle}
                          />
                        </UnitField>
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>위약금</FormFieldLabel>
                        <UnitField unit="원">
                          <FormInputText
                            control={control}
                            disabled={readOnly}
                            name="penaltyAmount"
                            placeholder="위약금"
                            sx={readOnly ? disabledInputStyle : textInputStyle}
                          />
                        </UnitField>
                      </FormFieldGroupHalf>
                    </FormFieldRow>

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>미수금</FormFieldLabel>
                        <UnitField unit="원">
                          <FormInputText
                            control={control}
                            disabled={readOnly}
                            name="outstandingAmount"
                            sx={readOnly ? disabledInputStyle : textInputStyle}
                          />
                        </UnitField>
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>환불금액</FormFieldLabel>
                        <UnitField unit="원">
                          <FormInputText
                            control={control}
                            disabled={readOnly}
                            name="refundAmount"
                            sx={readOnly ? disabledInputStyle : textInputStyle}
                          />
                        </UnitField>
                      </FormFieldGroupHalf>
                    </FormFieldRow>

                    <FormFieldRow>
                      <FormFieldGroupHalf>
                        <FormFieldLabel>결제금액</FormFieldLabel>
                        <UnitField unit="원">
                          <FormInputText
                            control={control}
                            disabled={readOnly}
                            name="paymentAmount"
                            sx={readOnly ? disabledInputStyle : textInputStyle}
                          />
                        </UnitField>
                      </FormFieldGroupHalf>
                      <FormFieldGroupHalf />
                    </FormFieldRow>
                  </ContentSection>
                )}

                {/* 결제/환불 수단 — 판매등록 결제 모달과 같은 ContentSection 카드 */}
                <ContentSection sx={{ marginBottom: '12px', position: 'relative' }}>
                  <PaymentLockContent locked={paymentLocked}>
                    {!splitEditMode && (
                      <FormFieldLabel>{updateType === 'refund' ? '환불수단' : '결제수단'}</FormFieldLabel>
                    )}
                    {splitEditMode ? (
                      /* 복합(분할) 건 — 단일 수단 폼 대신 라인 편집기. 합계=금액이어야 저장 가능. */
                      <SplitPaymentSection
                        plain
                        autoEditSingle={!paymentLocked}
                        refundMode={updateType === 'refund'}
                        totalAmount={
                          Number(
                            String(
                              (updateType === 'refund' ? watch('refundAmount') : watch('ticketFinalPrice')) || 0,
                            ).replace(/,/g, ''),
                          ) || 0
                        }
                        lines={splitEditLines}
                        onChange={setSplitEditLines}
                        formatInteger={value => (value === 0 ? '0' : Number(value || 0).toLocaleString())}
                        cashReceipt={{
                          approvalNumber: watch('approvalNumber'),
                          receiptYn: watch('receiptYn'),
                          deductionType: watch('deductionType'),
                          phoneNumber: watch('issuePhoneNumber'),
                          businessNumber: watch('issueBusinessNumber'),
                        }}
                        // 현금영수증·승인번호는 주문 레벨 1회 입력 — 판매등록 결제 모달과 같은 자리에 노출.
                        // 없으면 현금·계좌이체 건의 승인번호를 수정 모달에서 볼 수도 고칠 수도 없다.
                        beforeAddButton={currentMethod =>
                          (currentMethod === 'CASH' || currentMethod === 'TRANSFER' || splitHasCashLike) && (
                            <Box style={{ margin: '16px 0 4px' }}>
                              <CashPaymentForm control={control} setValue={setValue} readOnly={readOnly} />
                            </Box>
                          )
                        }
                      />
                    ) : (
                      <>
                        <FormInputDropdown
                          control={control}
                          disabled={readOnly}
                          sx={dropdownStyle}
                          name="paymentMethod"
                          options={payMethods}
                          disabledItem={<MenuItem disabled>{t('Select Payment Method')}</MenuItem>}
                        />

                        {watch('paymentMethod') === '카드' && (
                          <CardPaymentForm control={control} setValue={setValue} readOnly={readOnly} />
                        )}
                        {watch('paymentMethod') === '현금' && (
                          <CashPaymentForm control={control} setValue={setValue} readOnly={readOnly} />
                        )}
                        {watch('paymentMethod') === '계좌이체' && (
                          <BankTransferForm control={control} setValue={setValue} readOnly={readOnly} />
                        )}
                      </>
                    )}
                  </PaymentLockContent>
                  {paymentLocked && (
                    <PaymentLockOverlay>
                      <PaymentUnlockButton type="button" onClick={() => setPaymentLocked(false)}>
                        <PaymentUnlockIcon className="unlock-icon">
                          <EditOutlinedIcon sx={{ fontSize: 26 }} />
                        </PaymentUnlockIcon>
                        <PaymentUnlockLabel className="unlock-label">{t('수정하기')}</PaymentUnlockLabel>
                      </PaymentUnlockButton>
                    </PaymentLockOverlay>
                  )}
                </ContentSection>

                {/* 메모 — 결제수정 모달의 사유 카드와 동일한 전폭 배치 */}
                <ContentSection sx={{ marginBottom: '12px' }}>
                  <FormFieldLabel>{updateType === 'refund' ? '환불사유' : t('Memo')}</FormFieldLabel>
                  <FormInputTextArea
                    control={control}
                    minRows={4}
                    disabled={readOnly}
                    style={{
                      width: '100%',
                      height: '90px',
                      padding: '12px 13px 10px 11px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: '1px solid #a6a6a6',
                      borderRadius: '8px',
                      resize: 'none',
                    }}
                    placeholder={t('Please Enter a Note')}
                    name="memo"
                    rules={{ required: true }}
                  />
                </ContentSection>
              </>
            )}
          </ModalContent>

          {/* 하단 버튼 영역 */}
          <ModalFooter>
            <CancelButton onClick={handleClose}>{readOnly ? t('닫기') : t('Cancellation')}</CancelButton>
            {!readOnly && updateType === 'refund' && (
              <RefundCancelButton onClick={handleRefundCancel}>{t('환불취소')}</RefundCancelButton>
            )}
            {!readOnly && (
              <SaveButton onClick={handleSubmit(handleSaveClick)}>
                {t('Save')}
              </SaveButton>
            )}
          </ModalFooter>
        </ModalContainer>
      </StyledModal>

      {/* 확인 다이얼로그 */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">변경 확인</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {updateType === 'pay' && '결제정보를 변경하시겠습니까?'}
            {updateType === 'refund' && '환불정보를 변경하시겠습니까?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>취소</Button>
          <Button onClick={handleConfirmSubmit} autoFocus>
            확인
          </Button>
        </DialogActions>
      </Dialog>

      {/* 환불 취소 확인 다이얼로그 */}
      <Dialog
        open={openRefundCancelDialog}
        onClose={handleRefundCancelDialogClose}
        aria-labelledby="refund-cancel-dialog-title"
        aria-describedby="refund-cancel-dialog-description">
        <DialogTitle id="refund-cancel-dialog-title">환불 취소</DialogTitle>
        <DialogContent>
          <DialogContentText id="refund-cancel-dialog-description">환불을 취소하시겠습니까?</DialogContentText>
          {isLockerProduct && (
            <RefundCancelLockerWrapper>
              {/* 자리 배정은 환불취소가 정하지 않는다 — 이용권만 되살아나고 미배정으로 남는다.
                  배정은 회원상세/라커관리에서 따로 한다 (회원상세 환불취소와 동일 계약). */}
              <RefundCancelWarningText>이용권은 미배정으로 복원됩니다. 자리 배정은 회원상세에서 해주세요.</RefundCancelWarningText>
            </RefundCancelLockerWrapper>
          )}
          <RefundCancelReasonWrapper>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              placeholder="환불취소 사유 (선택)"
              value={refundCancelReason}
              onChange={e => setRefundCancelReason(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          </RefundCancelReasonWrapper>
        </DialogContent>
        <DialogActions>
          <CommonModalButton
            variant="cancel"
            onClick={handleRefundCancelDialogClose}
            disabled={cancelRefundMutation.isPending}>
            취소
          </CommonModalButton>
          <CommonModalButton
            variant="submit"
            onClick={handleConfirmRefundCancel}
            autoFocus
            disabled={cancelRefundMutation.isPending}>
            확인
          </CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 판매금액 인상 확인 — 금액 오타로 정가의 몇 배가 저장되는 것을 막는다 */}
      <ConfirmModal
        open={overPriceConfirmOpen}
        onClose={() => {
          setOverPriceConfirmOpen(false);
          setPendingSubmitValues(null);
        }}
        onConfirm={() => {
          setOverPriceConfirmOpen(false);
          const values = pendingSubmitValues;
          setPendingSubmitValues(null);
          if (values) submit(values);
        }}
        message={`판매금액을 ${formatInteger(originalTicketPrice)}원에서 ${formatInteger(
          toInt(ticketPrice),
        )}원으로 올려 저장합니다.\n이대로 수정할까요?`}
        highlightText={`${formatInteger(toInt(ticketPrice))}원`}
        highlightColor="#4AB3BC"
        cancelText="취소"
        confirmText="수정"
        confirmColor="#4AB3BC"
      />
    </div>
  );
};

export default ManualDetailModalV2;
